import type { ReactNode } from 'react';
import { FileText, ListChecks, Mail, MapPin, MessageSquare, Phone, Users, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';
import { TONE_PILL, type Tone } from '../../lib/tone';
import { Icon } from '../ui/icon';

export type ActivityType = 'call' | 'mail' | 'meeting' | 'visit' | 'note' | 'task' | 'quote';

/** Icon und Ton je Typ sind fest, damit der Verlauf überall gleich aussieht. */
export const ACTIVITY: Record<ActivityType, { icon: LucideIcon; tone: Tone; label: string }> = {
  call: { icon: Phone, tone: 'info', label: 'Anruf' },
  mail: { icon: Mail, tone: 'highlight', label: 'E-Mail' },
  meeting: { icon: Users, tone: 'warning', label: 'Termin' },
  visit: { icon: MapPin, tone: 'success', label: 'Vor-Ort' },
  note: { icon: MessageSquare, tone: 'neutral', label: 'Notiz' },
  task: { icon: ListChecks, tone: 'neutral', label: 'Aufgabe' },
  quote: { icon: FileText, tone: 'highlight', label: 'Angebot' },
};

/**
 * Interaktionstypen des CRM (InteractionType im Backend) mit Text und
 * Verlaufstyp. Rechnung erscheint im Verlauf wie ein Angebot, Videotermin wie
 * ein Termin, Sonstiges wie eine Notiz.
 */
export const INTERAKTION: Record<string, { label: string; type: ActivityType }> = {
  call: { label: 'Anruf', type: 'call' },
  call_outbound: { label: 'Ausgehender Anruf', type: 'call' },
  call_inbound: { label: 'Eingehender Anruf', type: 'call' },
  email: { label: 'E-Mail', type: 'mail' },
  meeting: { label: 'Termin', type: 'meeting' },
  video_call: { label: 'Videotermin', type: 'meeting' },
  site_visit: { label: 'Vor-Ort-Besuch', type: 'visit' },
  note: { label: 'Notiz', type: 'note' },
  task: { label: 'Aufgabe', type: 'task' },
  quote: { label: 'Angebot', type: 'quote' },
  invoice: { label: 'Rechnung', type: 'quote' },
  other: { label: 'Sonstiges', type: 'note' },
};

/** Text und Verlaufstyp einer Interaktion; Unbekanntes erscheint als Notiz mit dem Rohwert. */
export function interaktionInfo(typ: string): { label: string; type: ActivityType } {
  return INTERAKTION[typ] ?? { label: typ, type: 'note' };
}

interface ActivityItemBasis {
  /** Was passiert ist. */
  title: ReactNode;
  /** Wer · wann. */
  meta?: ReactNode;
  /** Text, z. B. Notiz oder Verlustgrund; Einzelheiten über `Angaben`. */
  children?: ReactNode;
  /** Am letzten Eintrag: keine Verbindungslinie. */
  last?: boolean;
  className?: string;
}

/**
 * Entweder ein Typ aus `ACTIVITY` (Icon und Ton fest, CRM und PM) oder ein
 * freies `icon` mit `tone` für Verläufe, die keine Kundenkontakte sind, etwa
 * die Änderungshistorie eines Förderprogramms im Radar.
 */
export type ActivityItemProps = ActivityItemBasis &
  ({ type: ActivityType; icon?: never; tone?: never } | { type?: never; icon: LucideIcon; tone?: Tone });

/** Ein Eintrag im Verlauf eines Kunden, Projekts oder Programms, verbunden durch eine Linie. */
export function ActivityItem({ type, icon, tone, title, meta, children, last, className }: ActivityItemProps) {
  const a = icon ? { icon, tone: tone ?? 'neutral', label: undefined } : (ACTIVITY[type!] ?? ACTIVITY.note);
  return (
    <div
      className={cn(
        'relative grid grid-cols-[32px_1fr] gap-x-3',
        !last && 'before:absolute before:bottom-1 before:left-[15.5px] before:top-9 before:w-px before:bg-border',
        className,
      )}
    >
      <span className={cn('flex size-8 items-center justify-center rounded-full', TONE_PILL[a.tone])} title={a.label}>
        <Icon icon={a.icon} size={15} />
      </span>
      <div className={cn('min-w-0 pt-1.5', last ? 'pb-0' : 'pb-5')}>
        {/* Meta rechts neben dem Titel, solange beides passt; sonst bricht sie in
            die nächste Zeile um, statt über den Rand zu schieben (390 px). */}
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
          <span className="min-w-0 font-medium [overflow-wrap:anywhere]">{title}</span>
          {meta && <span className="min-w-0 t-small text-muted-foreground">{meta}</span>}
        </div>
        {children && <div className="mt-1 text-muted-foreground [overflow-wrap:anywhere]">{children}</div>}
      </div>
    </div>
  );
}
